/**
 * React-City Logo — City skyline SVG icon
 * Variants: default, small, glow
 */

interface LogoProps {
  size?: number;
  className?: string;
  variant?: 'default' | 'small' | 'glow';
}

export default function CityLogo({ size = 32, className = '', variant = 'default' }: LogoProps) {
  const id = `city-grad-${variant}`;

  if (variant === 'small') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="React-City"
      >
        <rect x="4" y="12" width="6" height="16" rx="1" fill="currentColor" opacity="0.7" />
        <rect x="5" y="14" width="1.5" height="2" rx="0.3" fill="#60a5fa" opacity="0.9" />
        <rect x="7" y="14" width="1.5" height="2" rx="0.3" fill="#fde047" opacity="0.8" />
        <rect x="5" y="18" width="1.5" height="2" rx="0.3" fill="#60a5fa" opacity="0.7" />
        <rect x="12" y="6" width="8" height="22" rx="1.5" fill="currentColor" />
        <rect x="13.5" y="8" width="2" height="2.5" rx="0.4" fill="#60a5fa" opacity="0.9" />
        <rect x="16.5" y="8" width="2" height="2.5" rx="0.4" fill="#fde047" opacity="0.8" />
        <rect x="13.5" y="12" width="2" height="2.5" rx="0.4" fill="#60a5fa" opacity="0.7" />
        <rect x="16.5" y="12" width="2" height="2.5" rx="0.4" fill="#60a5fa" opacity="0.9" />
        <rect x="13.5" y="16" width="2" height="2.5" rx="0.4" fill="#fde047" opacity="0.7" />
        <rect x="16.5" y="16" width="2" height="2.5" rx="0.4" fill="#60a5fa" opacity="0.8" />
        <rect x="22" y="10" width="6" height="18" rx="1" fill="currentColor" opacity="0.8" />
        <rect x="23" y="12" width="1.5" height="2" rx="0.3" fill="#fde047" opacity="0.9" />
        <rect x="25.5" y="12" width="1.5" height="2" rx="0.3" fill="#60a5fa" opacity="0.7" />
        <rect x="23" y="16" width="1.5" height="2" rx="0.3" fill="#60a5fa" opacity="0.8" />
        {/* Antenna */}
        <line x1="16" y1="6" x2="16" y2="2" stroke="currentColor" strokeWidth="1" opacity="0.6" />
        <circle cx="16" cy="2" r="1" fill="#ef4444" opacity="0.9" />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="React-City"
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#22c55e" />
          <stop offset="100%" stopColor="#06b6d4" />
        </linearGradient>
        {variant === 'glow' && (
          <filter id="city-glow">
            <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#22c55e" floodOpacity="0.5" />
          </filter>
        )}
      </defs>

      <g filter={variant === 'glow' ? 'url(#city-glow)' : undefined}>
        {/* Building Left */}
        <rect x="5" y="20" width="9" height="24" rx="1.5" fill={`url(#${id})`} opacity="0.75" />
        <rect x="7" y="22" width="2" height="3" rx="0.5" fill="#fff" opacity="0.85" />
        <rect x="10" y="22" width="2" height="3" rx="0.5" fill="#fff" opacity="0.6" />
        <rect x="7" y="27" width="2" height="3" rx="0.5" fill="#fde047" opacity="0.7" />
        <rect x="10" y="27" width="2" height="3" rx="0.5" fill="#fff" opacity="0.5" />
        <rect x="7" y="32" width="2" height="3" rx="0.5" fill="#fff" opacity="0.65" />
        <rect x="10" y="32" width="2" height="3" rx="0.5" fill="#fde047" opacity="0.6" />

        {/* Building Center (tallest) */}
        <rect x="16" y="8" width="12" height="36" rx="2" fill={`url(#${id})`} />
        <rect x="18.5" y="11" width="2.5" height="3.5" rx="0.5" fill="#fff" opacity="0.9" />
        <rect x="22.5" y="11" width="2.5" height="3.5" rx="0.5" fill="#fde047" opacity="0.8" />
        <rect x="18.5" y="17" width="2.5" height="3.5" rx="0.5" fill="#fff" opacity="0.7" />
        <rect x="22.5" y="17" width="2.5" height="3.5" rx="0.5" fill="#fff" opacity="0.85" />
        <rect x="18.5" y="23" width="2.5" height="3.5" rx="0.5" fill="#fde047" opacity="0.7" />
        <rect x="22.5" y="23" width="2.5" height="3.5" rx="0.5" fill="#fff" opacity="0.75" />
        <rect x="18.5" y="29" width="2.5" height="3.5" rx="0.5" fill="#fff" opacity="0.6" />
        <rect x="22.5" y="29" width="2.5" height="3.5" rx="0.5" fill="#fde047" opacity="0.65" />
        <rect x="18.5" y="35" width="2.5" height="3.5" rx="0.5" fill="#fff" opacity="0.5" />
        <rect x="22.5" y="35" width="2.5" height="3.5" rx="0.5" fill="#fff" opacity="0.7" />

        {/* Antenna */}
        <line x1="22" y1="8" x2="22" y2="3" stroke={`url(#${id})`} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="22" cy="2.5" r="1.5" fill="#ef4444" />

        {/* Building Right */}
        <rect x="30" y="16" width="10" height="28" rx="1.5" fill={`url(#${id})`} opacity="0.85" />
        <rect x="32" y="18" width="2" height="3" rx="0.5" fill="#fff" opacity="0.8" />
        <rect x="35.5" y="18" width="2" height="3" rx="0.5" fill="#fde047" opacity="0.75" />
        <rect x="32" y="23" width="2" height="3" rx="0.5" fill="#fde047" opacity="0.7" />
        <rect x="35.5" y="23" width="2" height="3" rx="0.5" fill="#fff" opacity="0.6" />
        <rect x="32" y="28" width="2" height="3" rx="0.5" fill="#fff" opacity="0.75" />
        <rect x="35.5" y="28" width="2" height="3" rx="0.5" fill="#fff" opacity="0.5" />
        <rect x="32" y="33" width="2" height="3" rx="0.5" fill="#fde047" opacity="0.65" />
      </g>

      {/* Ground line */}
      <line x1="2" y1="44" x2="46" y2="44" stroke={`url(#${id})`} strokeWidth="1" opacity="0.3" strokeLinecap="round" />
    </svg>
  );
}
